import { useRouter } from "next/router";
import { routes } from "../../src/routes";

import { getAllBlogs, getBlogBySlug } from "../../src/services/blog";

import {
  Box,
  Breadcrumbs,
  Container,
  Grid,
  Link,
  Typography,
} from "@mui/material";
import Head from "next/head";
import DraftAsHtml from "../../src/components/DraftAsHtml";
import RecentBlogs from "../../src/components/RecentBlogs";
import { IMG_PLACEHOLDER } from "../../src/constants";
import { COLORS } from "../../src/theme";

// mutate blogItem objects to serialize dates to strings
const serializeDates = (blogItem) => {
  blogItem.createdAt = blogItem.createdAt.toISOString();
  if (blogItem.updatedAt) {
    blogItem.updatedAt = blogItem.updatedAt.toISOString();
  }
};

export const getStaticPaths = async () => {
  const allBlogItems = await getAllBlogs();
  return {
    paths: allBlogItems.map((blogItem) => ({
      params: {
        slug: blogItem.seoTitle,
      },
    })),
    fallback: "blocking",
  };
};

export const getStaticProps = async (context) => {
  const blogItemData = await getBlogBySlug(context.params.slug);
  let recentPosts = await getAllBlogs({limit: 5});
  // only pass on the data that's necessary
  recentPosts = recentPosts.map(post => {
    return {
      title: post.title,
      seoTitle: post.seoTitle
    }
  });
  serializeDates(blogItemData);
  return { props: { blogItemData, recentPosts }, revalidate: 60 };
};

export default function BlogItem({ blogItemData, recentPosts }) {
  const router = useRouter();
  const slug = router.query.slug;

  if (!blogItemData || !blogItemData?.title || !blogItemData?.description) {
    return null;
  }

  const blog = blogItemData;

  let metaDescription = blogItemData?.metaDescription ?? "";
  let metaKeywords = `${
    blogItemData?.metaKeywords ?? ""
  }, speed dating, speed dating london, speed dating near me,  london speed dating,  speed dating in london,  singles party london,  singles events near me,  singles events london`;

  return (
    <Box py={2} bgcolor={COLORS.lightGrey} minHeight="inherit">
      <Head>
        <title>{`${blog.title} | True Dating`}</title>
        <meta name="description" content={metaDescription} />
        <meta name="keywords" content={metaKeywords} />
        <meta property="og:title" content={`${blog.title} | True Dating`} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:image" content={blog.imgURL} />
        <meta property="og:url" content={`${routes.blog}/${blog.slug}`} />
        <meta property="og:type" content="article" />
        <meta property="og:site_name" content="True Dating" />
        <meta property="og:locale" content="en_GB" />
      </Head>
      <Container>
        <Grid container spacing={2}>
          <Grid item md={8} xs={12}>
            <Container>
              <Breadcrumbs
                aria-label="breadcrumb"
                sx={(theme) => ({
                  backgroundColor: COLORS.lightGrey,
                  color: COLORS.shark,
                  paddingTop: 4,
                  [theme.breakpoints.down("sm")]: {
                    maxWidth: 300,
                  },
                  "& a": {
                    color: "grey",
                    textDecoration: "none",
                  },
                  "& a:hover": {
                    textDecoration: "underline",
                  },
                })}
              >
                <Link underline="hover" color="inherit" href={routes.home}>
                  Home
                </Link>
                <Link underline="hover" color="inherit" href={routes.blog}>
                  Blogs
                </Link>
                <Typography color="text.primary">{blog.title}</Typography>
              </Breadcrumbs>
              <Box
                sx={(theme) => ({
                  padding: theme.spacing(6, 0),
                  backgroundColor: COLORS.lightGrey,
                })}
              >
                <Box
                  id="blog-img"
                  sx={(theme) => ({
                    width: "100%",
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                    backgroundRepeat: "no-repeat",
                    height: "100%",
                    maxHeight: 500,
                    minHeight: 400,
                    borderRadius: theme.shape.borderRadius,
                    [theme.breakpoints.down("xs")]: {
                      minHeight: 220,
                    },
                    backgroundImage: `url(${blog.imgURL || IMG_PLACEHOLDER})`,
                  })}
                />
                {/* <Typography variant='h1' sx={(theme) => ({
                  paddingTop: theme.spacing(4),
                  paddingBottom: theme.spacing(2),
                  fontSize: 30,
                  fontWeight: theme.typography.fontWeightBold,
                  [theme.breakpoints.down('xs')]: {
                    fontSize: 30,
                  },
                })}>{blog.title}</Typography> */}
                <Box pb={2} pt={6}>
                  <DraftAsHtml
                    raw={JSON.parse(blog?.description)}
                  />
                </Box>
              </Box>
            </Container>
          </Grid>
          <Grid item md={4} display={{ xs: "none", md: "block" }}>
            <RecentBlogs blogItems={recentPosts} />
          </Grid>
        </Grid>
      </Container>
      <script
        type="application/ld+json"
        key="blogJsonLd"
        dangerouslySetInnerHTML={{
          __html: `{
        "@context": "https://schema.org",
        "@type": "Article",
        "mainEntityOfPage": {
          "@type": "WebPage",
          "@id": "https://truedating.co.uk${routes.blog}/${blog.seoTitle}"
        },
        "headline": "${blog.title}",
        "description": "${blog.metaDescription}",
        "image": "${blog.imgURL}",  
        "author": {
          "@type": "Organization",
          "name": "True Dating"
        },  
        "publisher": {
          "@type": "Organization",
          "name": "True Dating",
          "logo": {
            "@type": "ImageObject",
            "url": "https://truedating.co.uk/images/logo_pink.svg"
          }
        },
        "datePublished": "${new Date(blog.createdAt).toISOString()}"
      }`,
        }}
      />
    </Box>
  );
}
